"use client";

import { useAuth } from "@clerk/nextjs";
import { useSyncExternalStore } from "react";
import type { PricingConfiguration } from "modules/common/model/models";
import {
  appendPricingParams,
  DEFAULT_PRICING,
  pricingCacheKey,
  pricingForLocale,
  withPricingCacheKey,
} from "modules/common/pricing";
import { usePricingOverride } from "modules/common/components/Providers/PricingProvider";
import { useAppUser } from "modules/auth/user/hooks/useAppUser";

type PricingCacheKey =
  | PricingConfiguration
  | readonly ["account-pricing", string | null | undefined];

const subscribeToHydration = () => () => undefined;
const getClientHydrationState = () => true;
const getServerHydrationState = () => false;
const getServerPricing = () => DEFAULT_PRICING;

function getClientRegionalPricing() {
  const hostname = window.location.hostname;
  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]"
  ) {
    return DEFAULT_PRICING;
  }
  return pricingForLocale();
}

export function useRegionalPricing() {
  const pricing = useSyncExternalStore(
    subscribeToHydration,
    getClientRegionalPricing,
    getServerPricing
  );
  const isHydrated = useSyncExternalStore(
    subscribeToHydration,
    getClientHydrationState,
    getServerHydrationState
  );

  return { pricing, isHydrated };
}

/**
 * Explicit list/collection pricing always wins. For catalog requests, the
 * request-rendered context provides a correct first paint and the app-user
 * cache supersedes it once available. Anonymous requests keep an explicit
 * locale-derived source so their URL remains safe for edge caching.
 */
export function usePricingRequest(explicitPricing?: PricingConfiguration) {
  const { isLoaded, isSignedIn, userId } = useAuth();
  const { appUser } = useAppUser();
  const { pricing: anonymousPricing, isHydrated } = useRegionalPricing();
  const contextPricing = usePricingOverride();
  const pricingOverride =
    explicitPricing ??
    (isSignedIn
      ? (appUser?.preferences?.pricing ?? contextPricing)
      : contextPricing);
  const pricing: PricingConfiguration | undefined =
    pricingOverride ?? (isSignedIn ? undefined : anonymousPricing);
  const cacheKey: PricingCacheKey | undefined = pricingOverride
    ? pricingCacheKey(pricingOverride)
    : pricing
      ? pricingCacheKey(pricing)
      : ["account-pricing", userId];

  return {
    isAuthReady: pricingOverride ? true : isLoaded,
    // Public ISR data can carry the default EUR price. Keep the rest of that
    // cached card visible, but hide its price until auth and region are known.
    shouldMaskCachedPricing: pricingOverride ? false : !isHydrated || !isLoaded,
    pricing,
    cacheKey,
  };
}

export { appendPricingParams, withPricingCacheKey };

export function withoutPrice<T extends { price?: unknown }>(value: T): T {
  return { ...value, price: undefined };
}
